import {selectThreadById} from './store/selectors';
import type {AgentChatStore} from './store/store';
import type {ActiveThreadLoadFailure, AgentChatState} from './store/thread';

/** ThreadLoadOutcome is the state a thread's load settled on. */
export type ThreadLoadOutcome =
  | {status: 'loaded'}
  | {status: 'failed'; failure: ActiveThreadLoadFailure};

/**
 * waitForThreadLoad resolves with the thread's load outcome once no load is
 * in flight for it, or with null when the runtime cleared the load or never
 * started it.
 */
export function waitForThreadLoad(
  store: AgentChatStore,
  threadId: string
): Promise<ThreadLoadOutcome | null> {
  return new Promise(resolve => {
    const outcome = readOutcome(store.getState(), threadId);
    if (outcome !== 'loading') {
      resolve(outcome);
      return;
    }
    const unsubscribe = store.subscribe(() => {
      const next = readOutcome(store.getState(), threadId);
      if (next !== 'loading') {
        unsubscribe();
        resolve(next);
      }
    });
  });
}

function readOutcome(
  state: AgentChatState,
  threadId: string
): ThreadLoadOutcome | 'loading' | null {
  const load = state.threadLoads[threadId];
  if (load?.status === 'loading') {
    return 'loading';
  }
  if (load != null) {
    return load;
  }
  return selectThreadById(state, threadId)?.loadStatus === 'loaded'
    ? {status: 'loaded'}
    : null;
}
